import {
    EmployeeRequestService,
    type EmployeeRequestPatternRequest,
    type EmployeeRequest,
} from '@api/EmployeeRequestService';
import { useApiCall } from '../apiCall';
import { invalidateOrgEmployeeRequestData } from '@stores/orgEmployeeRequestStore';
import { useEffect, useMemo } from 'react';

interface UseEmployeeRequestPatternUpdateResult {
    updatedEmployeeRequest: EmployeeRequest | undefined;
    isEmployeeRequestPatternUpdateInProgress: boolean;
    updateEmployeeRequestPattern: () => void;
}

const useEmployeeRequestPatternUpdate = (
    departmentId: string | undefined,
    employeeId: string | undefined,
    requestId: string,
    employeeRequestPattern: EmployeeRequestPatternRequest,
): UseEmployeeRequestPatternUpdateResult => {
    const putParam = useMemo(
        () =>
            [
                departmentId,
                employeeId,
                requestId,
                employeeRequestPattern,
            ] as const,
        [departmentId, employeeId, requestId, employeeRequestPattern],
    );

    const {
        response: updatedEmployeeRequest,
        isLoading: isEmployeeRequestPatternUpdateInProgress,
        requestApiCall: updateEmployeeRequestPattern,
    } = useApiCall(
        EmployeeRequestService.putEmployeeRequestPattern,
        [...putParam],
        [...putParam],
        false,
    );

    /*
     * Pattern updates invalidate the cached org employee request
     * data, so the cache never serves the pre-update requests.
     */
    useEffect(() => {
        if (updatedEmployeeRequest !== undefined) {
            invalidateOrgEmployeeRequestData();
        }
    }, [updatedEmployeeRequest]);

    return {
        updatedEmployeeRequest,
        isEmployeeRequestPatternUpdateInProgress,
        updateEmployeeRequestPattern,
    };
};

export default useEmployeeRequestPatternUpdate;
